import { FAIcon } from '@/components'
import { Button } from '@/components/ui'
import { format } from '@/utils/date-fns'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay } from 'date-fns'
import React from 'react'
import { DELETE_EVENT_MUTATION, PATCH_EVENT_MUTATION } from '../mutations'
import type {
  DeleteEventMutation,
  EventNode,
  PatchEventMutation,
  PatchEventMutationVariables,
} from '../types.graphql'
import { eventFormToInput } from '../util'
import { EditEventPrompt } from './edit-event-prompt'
import { EventAttendees } from './event-attendees'
import { EventExportButton } from './event-export-button'
import type { EventFormValues } from './event-form-fields'
import { eventFormValuesFromEvent } from './event-form-fields'
import { EventResponseForm } from './event-response-form'

interface Props {
  event: EventNode
  /** Called after the event was removed, so the thread can drop the panel. */
  onDeleted?(): void
}

const Detail: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-semibold uppercase text-stone-500">
      {label}
    </span>
    <span className="text-sm text-stone-900">{value}</span>
  </div>
)

/** The event block on a forum thread: when and where, your answer, the answers. */
export const EventPanel: React.FC<Props> = ({ event, onDeleted }) => {
  const translations = useTranslate({
    event: 'events.event',
    when: 'events.when',
    location: 'common.location',
    deadline: 'events.registration-deadline',
    closed: 'events.registration-closed',
    edit: 'common.edit',
    remove: 'common.remove',
    deleteTitle: 'events.remove-event',
    deleteConfirm: 'events.remove-event-confirm',
    updated: 'events.event-updated',
    deleted: 'events.event-deleted',
    error: 'events.event-error',
  })

  const addToast = useToast()
  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()

  // The backend only hands the answers to the event creator and to
  // administrators, so their presence is also the answer to "may I manage it".
  const mayManage =
    event.summary !== null && hasPermissions(PERMISSIONS.events.change.event)
  const mayDelete =
    event.summary !== null && hasPermissions(PERMISSIONS.events.delete.event)

  const [patchEvent] = useMutation<
    PatchEventMutation,
    PatchEventMutationVariables
  >(PATCH_EVENT_MUTATION, {
    onCompleted: () => addToast('success', translations.updated),
    onError: onErrorAuto(translations.error),
  })

  const [deleteEvent] = useMutation<DeleteEventMutation, { id: string }>(
    DELETE_EVENT_MUTATION,
    {
      onCompleted: () => {
        addToast('success', translations.deleted)
        onDeleted?.()
      },
      onError: onErrorAuto(translations.error),
      update: cache =>
        cache.evict({
          id: cache.identify({ __typename: 'EventNode', id: event.id }),
        }),
    }
  )

  const startsAt = new Date(event.startsAt)
  const endsAt = event.endsAt ? new Date(event.endsAt) : null

  async function handleEdit() {
    const { data: values } = await addPrompt<EventFormValues | null>(
      resolve => (
        <EditEventPrompt
          initialValues={eventFormValuesFromEvent(event)}
          onSubmit={resolve}
        />
      )
    )
    if (!values) return

    await patchEvent({
      variables: { id: event.id, ...eventFormToInput(values) },
    })
  }

  async function handleDelete() {
    const { data: confirmed } = await confirm(
      translations.deleteConfirm,
      translations.deleteTitle
    )
    if (!confirmed) return

    await deleteEvent({ variables: { id: event.id } })
  }

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <FAIcon
            className="text-stone-500"
            icon="calendar-star"
            type="regular"
            fixedWidth
          />
          <h3 className="m-0 text-base font-semibold text-stone-900">
            {translations.event}
          </h3>
        </div>

        <div className="flex gap-2">
          {event.summary !== null && <EventExportButton eventId={event.id} />}

          {mayManage && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleEdit}
            >
              {translations.edit}
            </Button>
          )}
          {mayDelete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDelete}
            >
              {translations.remove}
            </Button>
          )}
        </div>
      </header>

      <div className="flex flex-col gap-4 px-5 py-4 sm:px-6">
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          <Detail
            label={translations.when}
            value={
              endsAt
                ? `${format(startsAt, 'PPp')} – ${format(
                    endsAt,
                    isSameDay(startsAt, endsAt) ? 'p' : 'PPp'
                  )}`
                : format(startsAt, 'PPp')
            }
          />

          {!!event.location && (
            <Detail label={translations.location} value={event.location} />
          )}

          <Detail
            label={translations.deadline}
            value={
              <>
                {format(new Date(event.registrationClosesAt), 'PPp')}
                {!event.registrationOpen && (
                  <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-600">
                    {translations.closed}
                  </span>
                )}
              </>
            }
          />
        </div>

        <EventResponseForm event={event} />

        <EventAttendees event={event} mayAnswerForOthers={mayManage} />
      </div>
    </section>
  )
}
